// src/components/AscendDialog.js
import React from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, Box, Typography, Stack, Divider
} from '@mui/material';

const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';
const BLACK_DEEP = '#0a0a0a';

export default function AscendDialog({
    open,
    onClose,
    onConfirm,
    cost = 0,
    userSoulDust = 0,
    cardName = 'This Card',
    rarity = 'Common',
    currentAscensions = 0,
}) {
    const canAfford = userSoulDust >= cost;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    background: `linear-gradient(180deg, rgba(0,0,0,0.85) 0%, ${BLACK_DEEP} 100%)`,
                    border: `1px solid ${GOLD_DIM}`,
                    boxShadow: `0 24px 64px rgba(0,0,0,0.7), 0 0 24px ${GOLD_SOFT}`,
                    color: TEXT_WARM,
                    borderRadius: 2,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogContent
                sx={{
                    pt: 3,
                    background: `
            radial-gradient(900px 360px at 50% 120%, rgba(212,175,55,0.08), transparent 60%),
            radial-gradient(700px 280px at -10% -20%, rgba(212,175,55,0.05), transparent 60%)
          `,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ color: GOLD, fontFamily: `'Cinzel', serif`, fontWeight: 800, letterSpacing: 0.8, mb: 1 }}
                >
                    Ascend
                </Typography>

                <Typography variant="body2" sx={{ color: TEXT_WARM, mb: 2 }}>
                    *“Only through the ashes of the fallen do legends rise anew.”*
                </Typography>

                <Box
                    sx={{
                        p: 2,
                        border: `1px solid ${GOLD_DIM}`,
                        borderRadius: 2,
                        background: 'rgba(0,0,0,0.35)',
                        boxShadow: `0 0 16px rgba(212,175,55,0.12) inset`,
                        mb: 2,
                    }}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ color: GOLD_DIM }}>
                            Card
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: TEXT_WARM, fontWeight: 700 }}>
                            {cardName}
                        </Typography>

                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="body2" sx={{ color: GOLD_DIM }}>Rarity</Typography>
                                <Typography variant="body2" sx={{ color: TEXT_WARM }}>{rarity}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ color: GOLD_DIM }}>Ascensions</Typography>
                                <Typography variant="body2" sx={{ color: TEXT_WARM }}>{currentAscensions}</Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 1.5, borderColor: GOLD_DIM }} />

                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: GOLD_DIM }}>Cost</Typography>
                            <Typography variant="body2" sx={{ color: TEXT_WARM, fontWeight: 700 }}>
                                {cost} SD
                            </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: GOLD_DIM }}>Your Soul Dust</Typography>
                            <Typography variant="body2" sx={{ color: TEXT_WARM }}>
                                {userSoulDust} SD
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>

                <Typography variant="body2" sx={{ color: TEXT_WARM }}>
                    Ascending will <strong>raise this card’s level by +10</strong>.
                    The card must be at its current maximum level and final evolution.
                </Typography>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${GOLD_DIM}`,
                    background: `linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3))`,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: TEXT_WARM,
                        border: `1px solid ${GOLD_DIM}`,
                        background: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.7) 100%)`,
                        borderRadius: 2,
                        '&:hover': { borderColor: GOLD },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={!canAfford}
                    sx={{
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        color: '#000',
                        background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                        border: `1px solid ${GOLD_DIM}`,
                        boxShadow: `0 0 14px ${GOLD_SOFT}, 0 6px 18px rgba(0,0,0,0.45)`,
                        borderRadius: 2,
                        '&:hover': {
                            background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                            boxShadow: `0 0 18px ${GOLD_SOFT}, 0 8px 22px rgba(0,0,0,0.55)`,
                        },
                    }}
                >
                    Ascend (+10)
                </Button>
            </DialogActions>
        </Dialog>
    );
}
